/**
 * API utilities for Verstaani.
 *
 * Helpers for handling paginated API responses consistently across the application.
 */

/**
 * Extracts array data from API response, handling both paginated and non-paginated formats.
 *
 * Paginated responses have: { results: [...], count: N, next: url, previous: url }
 * Non-paginated responses have: [...] or { data: [...] }
 *
 * @param {object} response - Axios response object
 * @returns {{ data: Array, total: number }} - Extracted array and total count
 */
export const extractListFromResponse = (response) => {
  const rawData = response.data.results ?? response.data;
  const data = Array.isArray(rawData) ? rawData : [];
  const total = response.data.count ?? data.length;
  return { data, total };
};

/**
 * Checks if response has more items than fetched (for "showing first X" indicator).
 *
 * @param {object} response - Axios response object
 * @param {number} pageSize - Page size used in request
 * @returns {boolean} - True if there are more items
 */
export const hasMoreItems = (response, pageSize) => {
  const rawData = response.data.results ?? response.data;
  const total = response.data.count ?? (Array.isArray(rawData) ? rawData.length : 0);
  return total > pageSize;
};

/**
 * Flattens a DRF error response into a single user-displayable message.
 *
 * Handles the shapes DRF actually produces: a plain string body, {detail},
 * {message}, {non_field_errors: [...]}, and per-field error maps
 * ({field: ["msg", ...]}), falling back to the given message.
 *
 * Pass preferredFields when the caller knows which fields carry a message meant for
 * the user: the first one present is returned on its own, without the "field: "
 * prefix the generic field map adds.
 *
 * @param {object} error - Axios error object
 * @param {string} fallbackMessage - Message to use when nothing usable is found
 * @param {string[]} [preferredFields] - Field names to read first, in priority order
 * @returns {string}
 */
export const flattenDrfError = (error, fallbackMessage, preferredFields = []) => {
  const data = error.response?.data;
  if (!data) {
    return error.message || fallbackMessage;
  }
  if (typeof data === 'string') {
    return data;
  }
  for (const field of preferredFields) {
    const value = data[field];
    if (value) {
      return Array.isArray(value) ? value.join(', ') : String(value);
    }
  }
  if (data.detail) {
    return data.detail;
  }
  if (data.message) {
    return data.message;
  }
  if (data.non_field_errors) {
    return Array.isArray(data.non_field_errors) ? data.non_field_errors.join(', ') : data.non_field_errors;
  }
  const fieldErrors = Object.entries(data)
    .map(([field, errors]) => {
      const errorMsg = Array.isArray(errors) ? errors.join(', ') : String(errors);
      return `${field}: ${errorMsg}`;
    })
    .join('; ');
  return fieldErrors || fallbackMessage;
};
